import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Typography, Space } from 'antd';
import { 
  DatabaseOutlined, 
  DollarOutlined, 
  PlayCircleOutlined, 
  StopOutlined,
  PlusOutlined,
  EyeOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import RechargeModal from '../components/RechargeModal';

const { Title } = Typography;

interface DashboardStats {
  servers: {
    total_servers: number;
    running_servers: number;
    active_servers: number;
  };
  orders: {
    total_orders: number;
    total_spent: number;
  };
}

interface Server {
  id: number;
  name: string;
  game_name: string;
  status: string;
  port: number;
  expires_at: string;
  node_name: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, serversResponse] = await Promise.all([
        api.get('/users/stats'),
        api.get('/users/servers')
      ]);
      
      setStats(statsResponse.data);
      setServers(serversResponse.data);
    } catch (error: any) {
      console.error('加载仪表板数据失败:', error);
      setError(error.response?.data?.message || error.message || '无法连接到服务器');
      // 设置默认值防止组件崩溃
      setStats({
        servers: { total_servers: 0, running_servers: 0, active_servers: 0 },
        orders: { total_orders: 0, total_spent: 0 }
      });
      setServers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      running: { color: 'green', text: '运行中' },
      stopped: { color: 'red', text: '已停止' },
      starting: { color: 'orange', text: '启动中' },
      installing: { color: 'blue', text: '安装中' },
      error: { color: 'red', text: '错误' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const columns = [
    {
      title: '服务器名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '游戏类型',
      dataIndex: 'game_name',
      key: 'game_name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '端口',
      dataIndex: 'port',
      key: 'port',
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string) => {
        const expireDate = new Date(date);
        const now = new Date();
        const isExpired = expireDate < now;
        return (
          <span className={isExpired ? 'text-red-500' : ''}>
            {expireDate.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Server) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => navigate(`/servers/${record.id}`)}
          >
            管理
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <div className="fade-in">
        <div className="mb-6">
          <Title level={2}>控制台</Title>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium">连接错误</h3>
            <p className="text-red-600">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-2">
              重新加载
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* 页面标题和欢迎区域 */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '28px',
        marginTop: '16px',
        marginBottom: '32px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-40px',
          right: '-40px',
          width: '160px',
          height: '160px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-20px',
          left: '-20px',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Title level={1} style={{ 
            color: 'white', 
            margin: 0, 
            fontSize: '28px',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            marginBottom: '8px'
          }}>
            控制台
          </Title>
          <div style={{
            fontSize: '16px',
            opacity: 0.9,
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontSize: '20px'
            }}>👋</span>
            欢迎回来，<span style={{ 
              fontWeight: 'bold',
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>{user?.username}</span>！
          </div>
          <div style={{
            fontSize: '14px',
            marginTop: '8px',
            opacity: 0.8,
            fontWeight: '400'
          }}>
            今天是 {new Date().toLocaleDateString('zh-CN', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              weekday: 'long'
            })}，祝您使用愉快！
          </div>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-card">
            <Statistic
              title="总服务器数"
              value={stats?.servers.total_servers || 0}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-card">
            <Statistic
              title="运行中"
              value={stats?.servers.running_servers || 0}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>账户余额</span>
              <Button
                type="primary"
                size="small"
                icon={<WalletOutlined />}
                onClick={() => setRechargeModalVisible(true)}
                style={{ 
                  height: '24px',
                  fontSize: '12px',
                  padding: '0 8px'
                }}
              >
                充值
              </Button>
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#faad14',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <DollarOutlined />
              <span>{Number(user?.balance || 0).toFixed(2)}</span>
              <span style={{ fontSize: '14px', fontWeight: 'normal' }}>元</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-card">
            <Statistic
              title="本月消费"
              value={stats?.orders.total_spent || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#f5222d' }}
              suffix="元"
            />
          </Card>
        </Col>
      </Row>

      {/* 快速操作 */}
      <Card 
        className="mb-6" 
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '4px',
              height: '24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '2px'
            }} />
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'var(--text-color)'
            }}>
              快速操作
            </span>
          </div>
        }
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e8e8e8',
          overflow: 'hidden'
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderBottom: '1px solid #dee2e6',
          padding: '16px 24px'
        }}
        bodyStyle={{
          padding: '24px'
        }}
      >
        <div style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            onClick={() => navigate('/servers/create')}
            style={{
              height: '48px',
              minWidth: '160px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.3)';
            }}
          >
            创建服务器
          </Button>
          <Button 
            icon={<DatabaseOutlined />} 
            size="large"
            onClick={() => navigate('/servers')}
            style={{
              height: '48px',
              minWidth: '160px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              border: 'none',
              color: 'white',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
            }}
          >
            管理服务器
          </Button>
        </div>
      </Card>

      {/* 服务器列表 */}
      <Card 
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '4px',
              height: '24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '2px'
            }} />
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'var(--text-color)'
            }}>
              我的服务器
            </span>
          </div>
        }
        className="mb-6" 
        style={{ 
          marginTop: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e8e8e8',
          overflow: 'hidden'
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderBottom: '1px solid #dee2e6',
          padding: '16px 24px'
        }}
        bodyStyle={{
          padding: '0'
        }}
      >
        <Table
          columns={columns}
          dataSource={servers}
          rowKey="id"
          loading={loading}
          pagination={{ 
            pageSize: 5,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            style: {
              padding: '16px 24px',
              background: '#fafafa',
              borderTop: '1px solid #f0f0f0'
            }
          }}
          scroll={{ x: 800 }}
          style={{
            borderRadius: '0 0 12px 12px'
          }}
        />
      </Card>

      {/* 充值模态框 */}
      <RechargeModal
        visible={rechargeModalVisible}
        onCancel={() => setRechargeModalVisible(false)}
        onSuccess={() => {
          setRechargeModalVisible(false);
          // 重新加载用户数据以更新余额
          loadDashboardData();
        }}
      />
    </div>
  );
};

export default Dashboard;



